/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import Render from '@ion/dls/src/render/Render';
import {FieldLabel} from '../components/forms/FieldLabel';
import {FileBrowseButton} from '../editors/controls/FileBrowseButton';
import {NotificationGeneralAlertIcon} from '../icons/NotificationGeneralAlertIcon';
import {dlsAlt, dlsColor} from '../theme/theme';
import {FILE_TYPES} from '../utils/ComponentUtils';
import {ImportParamsType} from './BulkUploadDialog';

// defines the prop definitions for bulk upload importer ...
export interface BulkUploadImporterType {
  entityLabel: string;
  entity: string;
  uploadTitle: string;
  onImport: (file: FileList) => void;
  importParams: ImportParamsType;
  size?: string;
  id: string;
  disabled?: boolean;
  omitInstructions?: boolean;
  alternateInstructions?: string;
  alternateDropZoneInstructions?: string;
  isFormField?: boolean;
  isRequired?: boolean;
  isCurrentTheme?: boolean;
  customInstructions?: string;
}

const getDisabledColor = (disabled: boolean, isCurrentTheme: boolean) => (
  dlsColor(disabled ? 'grey5' : (isCurrentTheme ? 'darkestBlue' : 'aaBlue'))
);

// defines the styles used by bulk upload importer ...
export const useStyles = makeStyles(theme => ({
  uploadContainer : {
    display        : 'flex',
    justifyContent : 'center',
    flexDirection  : 'column',
    alignItems     : 'center',
    marginTop      : ({isFormField}: BulkUploadImporterType) => !isFormField && '30px',
    padding        : '40px 15px',
    background     : dlsColor('white'),
    border         : ({
      disabled,
      isFormField,
      isCurrentTheme
    }: BulkUploadImporterType) => !(disabled || isFormField) && `1px dashed ${getDisabledColor(disabled, isCurrentTheme)}`,
    boxSizing : 'border-box'
  },
  icon : {
    display       : 'inline-block',
    verticalAlign : 'middle',
    paddingTop    : 4,
    marginRight   : 24
  },
  message : {
    ...dlsAlt('typography', 'label2'),
    color         : ({disabled, isCurrentTheme}: BulkUploadImporterType) => getDisabledColor(disabled, isCurrentTheme),
    textTransform : 'upperCase',
    fontWeight    : 'bold',
    width         : '132px',
    margin        : '0 auto',
    textAlign     : 'center'
  },
  results : {
    ...dlsAlt('components', 'uploadResults', 'container'),
    display    : 'flex',
    alignItems : 'center',
    padding    : '0 10px',
    '& > p'    : {
      margin : '10px 0'
    }
  },
  uploadTitle : {
    textTransform : 'uppercase'
  },
  // the current theme's upload step - the DLS body type, Inter, not the
  // legacy theme's upper-case heading ...
  currentUploadTitle : {
    color         : dlsColor('grey1'),
    fontFamily    : 'Inter',
    fontSize      : 16,
    fontWeight    : 600,
    lineHeight    : '20px',
    textTransform : 'none'
  },
  currentInstructions : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 400,
    lineHeight : '20px'
  },
  disabled : {
    pointerEvents : 'none'
  },
  centerIconText : {
    alignItems     : 'center',
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'center'
  }
}) as any);

/**
 * Wrapper component for Bulk Upload Importer.
 *
 * ```typescript
 *
 * import {BulkUploadImporter} from '@ion/dls/src/imports';
 *
 * <BulkUploadImporter
 *   entity={'asset'}
 *   uploadTitle='Upload Assets'
 * >
 * </BulkUploadImporter>
 * ```
 *
 * @param entityLabel The current CTCEntity label under bulk import
 * @param id The upload container id
 * @param uploadTitle The upload container title
 * @param onImport callback to be executed when user initiates import post successsful upload
 * @param importParams The params to initiate import
 * @param size The size of the file which was uploaded
 * @param omitInstructions Whether to include the instructions
 * @param alternateInstructions Include factory password retrieval instructions
 * @param alternateDropZoneInstructions Include alternate drop zone instructions
 */
export const BulkUploadImporter = ({
  entityLabel,
  entity,
  id,
  uploadTitle,
  onImport,
  importParams,
  size = '',
  disabled = false,
  isCurrentTheme = false,
  omitInstructions = false,
  customInstructions = '',
  alternateInstructions = null,
  alternateDropZoneInstructions = null,
  isFormField = false,
  isRequired = false
}: BulkUploadImporterType) => {
  const classes = useStyles({disabled, isFormField, isCurrentTheme});

  const {
    fileName,
    fileSize,
    isComplete,
    isFailure,
    items
  } = importParams || {};

  return (
    <div>
      {isFormField ? (
        <FieldLabel label={uploadTitle} isRequired={isRequired} />
      ): (
        <h3 className={isCurrentTheme ? classes.currentUploadTitle : classes.uploadTitle}>{uploadTitle}</h3>
      )}
      <Render.If condition={customInstructions}>
        <p className={isCurrentTheme ? classes.currentInstructions : undefined}>{customInstructions}
        </p>
      </Render.If>
      {!omitInstructions && (
        <Render>
          <Render.If condition={!isNil(alternateInstructions)}>
            <p className={isCurrentTheme ? classes.currentInstructions : undefined}>
              {translate('common:editors.import.retrieveInstructions',
                {entity: entityLabel?.toLowerCase()})}
            </p>
          </Render.If>
          <Render.Else>
            <p className={isCurrentTheme ? classes.currentInstructions : undefined}>
              {translate('common:editors.import.instructions',
                {entity: entityLabel?.toLowerCase()})}
            </p>
          </Render.Else>
        </Render>
      )}
      {isFailure && (
        <div className={classes.results}>
          <p>
            <div className={classes.icon}>
              <NotificationGeneralAlertIcon color={dlsColor('white')} />
            </div>
            {translate('common:editors.import.importErrorMsg')}
          </p>
        </div>
      )}
      <div id={id} className={classes.uploadContainer}>
        <div className={`${classes.centerIconText} ${disabled ? classes.disabled : ''}`}>
          <FileBrowseButton onImport={onImport}
            disabled={disabled}
            entity={entity}
            fileTypes={FILE_TYPES} />
          <p className={classes.message}>
            <Render>
              <Render.If condition={!isNil(alternateDropZoneInstructions)}>
                {translate('common:editors.import.dragAndDropIdentifiers')}
              </Render.If>
              <Render.Else>
                {translate('common:editors.import.dragAndDropMsg')}
              </Render.Else>
            </Render>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadImporter;
